/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { FC, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import Typography from "components/generals/Typography";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { useTranslation } from "react-i18next";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { TemplateInterface, QueryResultInterface, HeaderEnum } from "medicalsystem-shared";
import ButtonDialog from "components/generals/ButtonDialog";
import { useMaterialUIController } from "context/ThemeContext";
import { Modal } from "components/generals/Modal";
import TemplateForm from "components/forms/TemplateForm";
import { FormActionType } from "types";
import { getNewTemplateDataContent } from "utils/model.utils";

/**
 * Props for TemplateRecycleBinModal component.
 */
interface TemplateRecycleBinModalProps {
  open: boolean;
  onClose: () => void;
  onTemplateRestored?: () => void;
}

/**
 * Template Recycle Bin Modal component.
 * Displays deleted templates (isActive = false) and allows restoring or permanently deleting them.
 * 
 * Features:
 * - View deleted templates in a table
 * - Restore templates (set isActive = true)
 * - Permanently delete templates
 * - Translation support
 * - Permission-based actions
 * 
 * @param {TemplateRecycleBinModalProps} props - Component properties
 * @returns {JSX.Element} Recycle bin modal with deleted templates table
 */
const TemplateRecycleBinModal: FC<TemplateRecycleBinModalProps> = ({
  open,
  onClose,
  onTemplateRestored,
}) => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isLoading, _error, sendRequest] = useHttp();
  const [deletedTemplates, setDeletedTemplates] = useState<TemplateInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [viewTemplate, setViewTemplate] = useState<TemplateInterface>(getNewTemplateDataContent());
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);

  // Fetch deleted templates
  useEffect(() => {
    if (open) {
      const url = `${CoreAPI_URLs.HPI_TEMPLATE_GET_ALL}?isActive=false&page=${page}&pageSize=${pageSize}`;
      sendRequest(url, onGetDeletedTemplatesHandler, "GET");
    }
  }, [open, page, pageSize]);

  const onGetDeletedTemplatesHandler = (templatesResponse: QueryResultInterface<TemplateInterface>) => {
    setDeletedTemplates(templatesResponse.results);
    setRowsTotal(templatesResponse.total);
    setPageTotal(Math.ceil(templatesResponse.total / pageSize));
  };

  const restoreTemplate = (template: TemplateInterface) => {
    sendRequest<boolean, TemplateInterface>(
      `${CoreAPI_URLs.HPI_TEMPLATE_RESTORE}`,
      (result) => onRestoreHandler(result, template),
      "PUT",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: template.id }],
      { payload: template, successMessage: t("templatesDataContent.templateRestored") }
    );
  };

  const onRestoreHandler = (result: boolean, template: TemplateInterface) => {
    const updatedList = deletedTemplates.filter((t) => t.id !== template.id);
    setDeletedTemplates(updatedList);
    setRowsTotal(rowsTotal - 1);
    if (onTemplateRestored) {
      onTemplateRestored();
    }
  };

  const deletePermanently = (template: TemplateInterface) => {
    sendRequest<boolean, TemplateInterface>(
      `${CoreAPI_URLs.HPI_TEMPLATE_DELETE_PERMANENTLY}`,
      (result) => onDeletePermanentlyHandler(result, template),
      "DELETE",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: template.id }],
      { payload: template, successMessage: t("templatesDataContent.templateDeletedPermanently") }
    );
  };

  const onDeletePermanentlyHandler = (result: boolean, template: TemplateInterface) => {
    const updatedList = deletedTemplates.filter((t) => t.id !== template.id);
    setDeletedTemplates(updatedList);
    setRowsTotal(rowsTotal - 1);
  };

  const handleViewTemplate = (template: TemplateInterface) => {
    setViewTemplate(template);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewTemplate(getNewTemplateDataContent());
  };

  const getUITable = (list: TemplateInterface[]) => {
    return {
      columns: [
        {
          Header: t("prescriptiontemplateTable.title"),
          accessor: "title",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.type"),
          accessor: "type",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={t(`prescriptiontemplateTable.${value}`)} />,
        },
        {
          Header: t("prescriptiontemplateTable.version"),
          accessor: "version",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("prescriptiontemplateTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <Box display="flex" gap={1}>
              <Button
                name="viewTemplateButton"
                variant="contained"
                color="info"
                onClick={() => {
                  const template = list.find((t) => t.id === value);
                  if (template) {
                    handleViewTemplate(template);
                  }
                }}
              >
                {t("templatesDataContent.view")}
              </Button>
              <Button
                name="restoreTemplateButton"
                variant="contained"
                color="success"
                size="small"
                onClick={() => {
                  const template = list.find((t) => t.id === value);
                  if (template) {
                    restoreTemplate(template);
                  }
                }}
              >
                {t("templatesDataContent.restore")}
              </Button>
              <ButtonDialog
                name="deletePermanentlyButton"
                onAcceptHandle={() => {
                  const template = list.find((t) => t.id === value);
                  if (template) {
                    deletePermanently(template);
                  }
                }}
                buttonText={t("templatesDataContent.deletePermanently")}
                title={t("templatesDataContent.confirmDeletePermanently")}
                description={t("templatesDataContent.confirmDeletePermanentlyMessage")}
                labelledbyID={"DeletePermanentlyTemplate"}
                describedbyID={"DeletePermanentlyDescriptionId"}
                color={false}
                startIcon={false}
              />
            </Box>
          ),
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(deletedTemplates), [deletedTemplates, t]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#2d3748' : '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {t("templatesDataContent.recycleBinTitle")}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {deletedTemplates.length === 0 && !isLoading ? (
            <Typography variant="body1" textAlign="center" py={4}>
              {t("templatesDataContent.recycleBinEmpty")}
            </Typography>
          ) : (
            <DataTable
              table={dataTableData}
              entriesPerPage={{ defaultValue: pageSize }}
              showTotalEntries={true}
              pagination={{ variant: "gradient", color: "info" }}
              isSorted={true}
              noEndBorder={false}
              isLoading={isLoading}
            />
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button variant="contained" color="secondary" onClick={onClose}>
            {t("formActionButtons.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Template Modal - Using same component as TemplateTable */}
      <Modal
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
      >
        <TemplateForm
          readOnlyValue={true}
          modelList={deletedTemplates}
          setModelList={setDeletedTemplates}
          action={FormActionType.MODIFY}
          model={viewTemplate}
          onCloseAction={handleCloseViewDialog}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default TemplateRecycleBinModal;
